
import React, { useState, useEffect } from 'react';
import { TicketStatus, Employee } from '../types';
import { Button } from './Button';

interface UpdateStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestStatusUpdate: (ticketId: string, newStatus: TicketStatus, sendSms?: boolean) => void; // Changed from onUpdateStatus
  ticketId: string;
  currentStatus: TicketStatus;
  currentUser: Employee | null;
}

export const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  isOpen,
  onClose,
  onRequestStatusUpdate, // Changed prop name
  ticketId,
  currentStatus,
  currentUser,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<TicketStatus>(currentStatus);
  const [sendSmsNotification, setSendSmsNotification] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(currentStatus);
      setSendSmsNotification(false);
    }
  }, [isOpen, currentStatus]);

  if (!isOpen) return null;

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStatus(event.target.value as TicketStatus);
  };

  const handleSmsCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSendSmsNotification(event.target.checked);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onRequestStatusUpdate(ticketId, selectedStatus, showSmsOption ? sendSmsNotification : undefined); // Use new prop
  };

  const statusOptions = Object.values(TicketStatus);
  const showSmsOption = selectedStatus === TicketStatus.READY_FOR_PICKUP || selectedStatus === TicketStatus.COMPLETED;


  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[100]"
      aria-labelledby="update-status-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id="update-status-modal-title" className="text-xl font-semibold text-slate-800">
            Update Status for Ticket <span className="text-[#003794]">{ticketId}</span>
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <p className="text-sm text-slate-600">
              Current Status: <span className="font-semibold">{currentStatus}</span>
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Select New Status:
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
              {statusOptions.map((status) => (
                <label key={status} htmlFor={`status-${status}`} className="flex items-center p-3 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors cursor-pointer has-[:checked]:bg-blue-100 has-[:checked]:border-[#003794]">
                  <input
                    type="radio"
                    id={`status-${status}`}
                    name="ticketStatus"
                    value={status}
                    checked={selectedStatus === status}
                    onChange={handleStatusChange}
                    className="h-4 w-4 text-[#003794] border-slate-400 focus:ring-[#003794]"
                  />
                  <span className="ml-3 text-sm text-slate-700">{status}</span>
                </label>
              ))}
            </div>
          </div>

          {showSmsOption && (
            <div className="mb-6 p-3 bg-slate-50 border border-slate-200 rounded-md">
              <label htmlFor="sendSms" className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="sendSms"
                  name="sendSms"
                  checked={sendSmsNotification}
                  onChange={handleSmsCheckboxChange}
                  className="h-4 w-4 text-[#003794] border-slate-400 rounded focus:ring-[#003794]"
                />
                <span className="ml-3 text-sm font-medium text-slate-700">Send customer SMS notification</span>
              </label>
               <p className="ml-7 text-xs text-slate-500">Notify customer that their service is {selectedStatus === TicketStatus.READY_FOR_PICKUP ? 'ready for pickup' : 'complete'}.</p>
            </div>
          )}

          <div className="border-t border-slate-200 pt-4 flex justify-end space-x-3">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={selectedStatus === currentStatus && !showSmsOption}>
              Update Status
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
